import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  CalendarDays, Coins, Scale, Baby, HeartPulse, Trees, AlertTriangle, CheckCircle2,
  ClipboardCheck, Award, Footprints, TrendingUp, Tag,
} from 'lucide-react'
import {
  getWeeklyReports, getWeeklyReport, getWeeklyRollup, getScorecards, getIncentiveStatement,
} from '../../services/api'

const fmt = (n) =>
  n === null || n === undefined ? '—'
    : new Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }).format(Math.round(n))

const GRANULARITIES = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
  { key: 'quarter', label: 'Quarterly' },
  { key: 'year', label: 'Yearly' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'incentive', label: 'Incentive' },
]

// Render order for the scorecard streams (balance_check joined in S5;
// older frozen weeks simply don't carry it).
const STREAM_ORDER = [
  'grazing_lock', 'weighing', 'treatments_inventory', 'births',
  'deaths', 'expenses', 'sales_income', 'balance_check', 'census',
]

// S8: the digital twin's three lines. Renders only once the ranch has twin
// runs (facts.trajectories.available) — older frozen weeks simply omit it.
function TrajectorySection({ twin }) {
  if (!twin || !twin.available) return null
  const lines = twin.lines || {}
  const gaps = twin.gaps || {}
  const checks = twin.checks || []
  const behind = checks.filter((c) => c.status === 'behind')
  const ahead = checks.filter((c) => c.status === 'ahead')
  const t = (kg) => `${(kg / 1000).toFixed(1)} t`
  const ROWS = [
    ['actual', 'Your farm (measured)', 'text-orange-700'],
    ['base', 'If nothing had changed', 'text-blue-700'],
    ['best', 'Achievable (phased)', 'text-emerald-700'],
  ]
  return (
    <SectionCard
      icon={TrendingUp} title="The Twin — where the farm is heading"
      badge={<Pill ok={behind.length === 0}
                   okText="On trajectory"
                   badText={`${behind.length} check(s) behind`} />}
    >
      <div className="space-y-1.5 text-sm mb-4">
        {ROWS.map(([key, label, cls]) => lines[key] && (
          <div key={key} className="flex justify-between gap-4">
            <span className={`font-medium ${cls}`}>{label}</span>
            <span>
              <b>{t(lines[key].liveweight_kg)}</b>
              {lines[key].liveweight_kg_low != null &&
                lines[key].liveweight_kg_high != null &&
                lines[key].liveweight_kg_low !== lines[key].liveweight_kg_high && (
                <span className="text-gray-500"> ({t(lines[key].liveweight_kg_low)}–{t(lines[key].liveweight_kg_high)})</span>
              )}
              <span className="text-gray-500"> · {fmt(Math.round(lines[key].head))} head</span>
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        <div className="rounded-lg bg-green-50 p-3">
          <div className="text-xs uppercase tracking-wide text-green-800 font-semibold">Value created so far</div>
          <div className="font-bold">{t(gaps.value_created_kg || 0)} · UGX {fmt(Math.round(gaps.value_created_ugx || 0))}</div>
          <div className="text-xs text-gray-500">measured farm vs if nothing had changed</div>
        </div>
        <div className="rounded-lg bg-emerald-50 p-3">
          <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Still on the table</div>
          <div className="font-bold">{t(gaps.still_on_table_kg || 0)} · UGX {fmt(Math.round(gaps.still_on_table_ugx || 0))}</div>
          <div className="text-xs text-gray-500">the phased best case vs today</div>
        </div>
      </div>
      {behind.length > 0 && (
        <ul className="text-sm text-amber-800 space-y-1">
          {behind.map((c, i) => (
            <li key={i}>{c.metric}: measured {fmt(c.measured)} vs expected {fmt(c.expected)} ({fmt(c.low)}–{fmt(c.high)})</li>
          ))}
        </ul>
      )}
      {ahead.length > 0 && (
        <p className="text-sm text-green-800">
          Ahead of forecast: {ahead.map((c) => c.metric).join(', ')} — a pleasant surprise, recorded.
        </p>
      )}
      {twin.value_waterfall && twin.value_waterfall.distributable_value_ugx != null && (
        <p className="text-sm text-gray-700 mt-2">
          After recorded costs of UGX {fmt(twin.value_waterfall.recorded_costs_ugx || 0)},
          distributable additional value stands at{' '}
          <b>UGX {fmt(twin.value_waterfall.distributable_value_ugx)}</b>{' '}
          <span className="text-gray-500">— display only; the full waterfall lives on the Twin page.</span>
        </p>
      )}
    </SectionCard>
  )
}

function SectionCard({ icon: Icon, title, children, badge }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-green-700" />
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        </div>
        {badge}
      </div>
      {children}
    </div>
  )
}

function Pill({ ok, okText, badText }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
      <CheckCircle2 className="h-3.5 w-3.5" /> {okText}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
      <AlertTriangle className="h-3.5 w-3.5" /> {badText}
    </span>
  )
}

function StreamChip({ s }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm'
  if (!s.measured) {
    return (
      <div className={`${base} bg-gray-50 border-gray-200 text-gray-500`} title={s.detail}>
        {s.label} <span className="text-xs">not scored</span>
      </div>
    )
  }
  return s.pass ? (
    <div className={`${base} bg-green-50 border-green-200 text-green-800`} title={s.detail}>
      <CheckCircle2 className="h-4 w-4" /> {s.label}
    </div>
  ) : (
    <Link to="/reports/evidence-ledger"
      className={`${base} bg-red-50 border-red-200 text-red-800 hover:bg-red-100`}
      title={s.detail}>
      <AlertTriangle className="h-4 w-4" /> {s.label}
    </Link>
  )
}

function StreakStrip({ weeks, streak }) {
  if (!weeks?.length) return null
  // Oldest → newest, so the strip reads left-to-right in time.
  const ordered = [...weeks].reverse()
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <span>Streak:</span>
      <div className="flex gap-1">
        {ordered.map((w) => (
          <span key={w.week_start}
            title={`Week of ${w.week_start}: ${w.overall_pass ? 'PASS' : 'FAIL'}`}
            className={`h-3.5 w-3.5 rounded-sm ${w.overall_pass ? 'bg-green-500' : 'bg-red-400'}`} />
        ))}
      </div>
      <span className="font-medium">{streak} week{streak === 1 ? '' : 's'} passing</span>
    </div>
  )
}

function ScorecardSection({ scorecard, strip }) {
  if (!scorecard) return null
  const streams = STREAM_ORDER.map(k => scorecard.streams?.[k]).filter(Boolean)
  const failed = streams.filter(s => s.measured && !s.pass)
  const late = scorecard.late_entries || []
  return (
    <SectionCard
      icon={ClipboardCheck} title="Data Collection Scorecard"
      badge={
        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
          scorecard.overall_pass ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {scorecard.overall_pass ? 'PASS' : 'FAIL'}
        </span>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-600">
          {scorecard.streams_passed} of {scorecard.streams_measured} scored streams passed
          — the week's data-collection discipline. Correctness is tracked separately by the trust score.
        </p>
        <StreakStrip weeks={strip?.items?.map(i => ({ week_start: i.week_start, overall_pass: i.overall_pass }))
                           || scorecard.recent_weeks}
                     streak={strip?.streak ?? scorecard.streak ?? 0} />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {streams.map(s => <StreamChip key={s.key} s={s} />)}
      </div>
      {failed.length > 0 && (
        <ul className="text-sm text-red-800 space-y-1 mb-3">
          {failed.map(s => <li key={s.key}><b>{s.label}:</b> {s.detail}</li>)}
        </ul>
      )}
      {late.length > 0 && (
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-1">
            Late entries ({late.length}) — dated in an earlier week, entered after that week froze:
          </p>
          <ul className="space-y-0.5">
            {late.slice(0, 10).map((e, i) => (
              <li key={i}>
                {e.kind}: {e.description || '—'} (dated {e.dated}
                {e.amount !== null && e.amount !== undefined ? `, ${fmt(e.amount)}` : ''})
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  )
}

function IncentiveView({ ranchId }) {
  const now = new Date()
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const [month, setMonth] = useState(defaultMonth)
  const { data, isLoading } = useQuery({
    queryKey: ['incentiveStatement', ranchId, month],
    queryFn: () => getIncentiveStatement(ranchId, month),
    enabled: !!ranchId,
  })
  const STATUS_STYLE = {
    payable: 'bg-green-100 text-green-800',
    accruing: 'bg-blue-100 text-blue-800',
    blocked: 'bg-red-100 text-red-800',
    not_payable: 'bg-amber-100 text-amber-800',
  }
  return (
    <SectionCard
      icon={Award} title="Monthly incentive statement"
      badge={data && (
        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_STYLE[data.status] || 'bg-gray-100'}`}>
          {data.status.replace('_', ' ')}
        </span>
      )}
    >
      <div className="mb-4">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {data && (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="px-3 py-2 rounded-lg bg-gray-50 border">
              Weeks passed: <b>{data.weeks_passed}/{data.weeks_in_month}</b>
            </div>
            {data.bonus_per_week !== null && data.bonus_per_week !== undefined ? (
              <div className="px-3 py-2 rounded-lg bg-gray-50 border">
                Accrued: <b>{fmt(data.accrued)} {data.currency}</b>
                <span className="text-gray-500"> ({fmt(data.bonus_per_week)}/week)</span>
              </div>
            ) : (
              <div className="px-3 py-2 rounded-lg bg-gray-50 border text-gray-500">
                No weekly bonus configured in the operating profile.
              </div>
            )}
          </div>
          <table className="min-w-full text-sm mb-3">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-4">Week of</th><th className="py-1 pr-4">Result</th>
              <th className="py-1">Streams</th>
            </tr></thead>
            <tbody>
              {data.weeks.map(w => (
                <tr key={w.week_start} className="border-b border-gray-100">
                  <td className="py-1 pr-4 whitespace-nowrap">{w.week_start}</td>
                  <td className="py-1 pr-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      w.status === 'pass' ? 'bg-green-100 text-green-800'
                        : w.status === 'fail' ? 'bg-red-100 text-red-800'
                          : w.status === 'pending' ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'}`}>
                      {w.status}
                    </span>
                  </td>
                  <td className="py-1">
                    {w.streams_passed !== null && w.streams_passed !== undefined
                      ? `${w.streams_passed}/${w.streams_measured}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(data.blockers || []).length > 0 && (
            <div className="text-sm text-red-800">
              <p className="font-medium">Payout blocked by unresolved audit variance:</p>
              <ul className="space-y-0.5">
                {data.blockers.map(b => <li key={b.id}>[{b.severity}] {b.title}</li>)}
              </ul>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">{data.note}</p>
        </>
      )}
    </SectionCard>
  )
}

function MoneySection({ money }) {
  if (!money) return null
  const ledger = money.ledger || {}
  return (
    <SectionCard
      icon={Coins} title="Money"
      badge={<Pill ok={(money.missing_receipt_count || 0) === 0}
                   okText="All receipts attached"
                   badText={`${money.missing_receipt_count} missing receipt(s)`} />}
    >
      <h3 className="text-sm font-medium text-gray-700 mb-2">Expenses this week</h3>
      {(money.expenses_detail || []).length === 0 ? (
        <p className="text-sm text-gray-500 mb-4">No approved expenses recorded.</p>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-4">Date</th><th className="py-1 pr-4">Category</th>
              <th className="py-1 pr-4">Description</th><th className="py-1 pr-4">Paid by</th>
              <th className="py-1 pr-4 text-right">Amount</th><th className="py-1">Receipt</th>
            </tr></thead>
            <tbody>
              {money.expenses_detail.map((e, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 pr-4 whitespace-nowrap">{e.date}</td>
                  <td className="py-1 pr-4">{e.category}</td>
                  <td className="py-1 pr-4">{e.description}</td>
                  <td className="py-1 pr-4">{e.payer}</td>
                  <td className="py-1 pr-4 text-right">{fmt(e.amount)}</td>
                  <td className="py-1">{e.receipt ? '✓' : <span className="text-amber-600">✗</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="text-sm font-medium text-gray-700 mb-2">Income &amp; sales this week</h3>
      {(money.income_detail || []).length === 0 ? (
        <p className="text-sm text-gray-500 mb-4">No approved income recorded.</p>
      ) : (
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-4">Date</th><th className="py-1 pr-4">Type</th>
              <th className="py-1 pr-4">Description</th><th className="py-1 pr-4">Received by</th>
              <th className="py-1 text-right">Amount</th>
            </tr></thead>
            <tbody>
              {money.income_detail.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 pr-4 whitespace-nowrap">{r.date}</td>
                  <td className="py-1 pr-4">{r.type}</td>
                  <td className="py-1 pr-4">{r.description}</td>
                  <td className="py-1 pr-4">{r.received_by || '—'}</td>
                  <td className="py-1 text-right">{fmt(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(money.deposits_week || []).length > 0 && (
        <>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Owner contributions to the float this week</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {money.deposits_week.map((d, i) => (
              <div key={i} className="px-3 py-2 rounded-lg bg-gray-50 border text-sm">
                <span className="font-medium">{d.owner}</span>
                <span className="text-gray-600"> · {d.date} · {fmt(d.amount)}</span>
                {d.reference ? <span className="text-xs text-gray-400"> · {d.reference}</span> : null}
              </div>
            ))}
          </div>
        </>
      )}

      {(money.float?.balances || []).length > 0 && (
        <>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Float balances (prepaid contributions)</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {money.float.balances.map((b, i) => (
              <div key={i} className={`px-3 py-2 rounded-lg border text-sm ${
                b.in_arrears ? 'bg-red-50 border-red-200'
                  : b.balance < 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                <span className="font-medium">{b.owner}</span>{' '}
                <span className="text-gray-700">{fmt(b.balance)}</span>
                {b.in_arrears && <span className="text-xs text-red-600"> · behind on contributions</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {(money.owner_income_week || []).length > 0 && (
        <>
          <h3 className="text-sm font-medium text-gray-700 mb-2">This week's sales, by owner</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {money.owner_income_week.map((o, i) => (
              <div key={i} className="px-3 py-2 rounded-lg bg-gray-50 border text-sm">
                <span className="font-medium">{o.owner}</span>
                <span className="text-gray-600"> · owed {fmt(o.owed_from_sales)} · banked {fmt(o.banked)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {(ledger.owners || []).length > 0 && (
        <>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Who owes who (running balance)</h3>
          <div className="flex flex-wrap gap-2 mb-2">
            {ledger.owners.map((o, i) => (
              <div key={i} className={`px-3 py-2 rounded-lg border text-sm ${
                o.net > 0.5 ? 'bg-green-50 border-green-200'
                  : o.net < -0.5 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50'}`}>
                <span className="font-medium">{o.name}</span>{' '}
                <span className="text-gray-700">{fmt(o.net)}</span>
                {o.week_delta !== null && o.week_delta !== undefined && (
                  <span className="text-xs text-gray-500"> ({o.week_delta > 0 ? '+' : ''}{fmt(o.week_delta)} this week)</span>
                )}
              </div>
            ))}
          </div>
          {(ledger.settle_up || []).length > 0 && (
            <p className="text-sm text-gray-600">
              To settle up: {ledger.settle_up.map(s => `${s.from} → ${s.to} ${fmt(s.amount)}`).join(' · ')}
            </p>
          )}
          {ledger.balanced === false && (
            <p className="text-sm text-red-600 mt-1">
              Ledger imbalance detected — contact support (this indicates a system issue, not your data).
            </p>
          )}
        </>
      )}
    </SectionCard>
  )
}

function HerdSection({ rec, weights, unseen }) {
  return (
    <SectionCard
      icon={Scale} title="Herd & weights"
      badge={rec && <Pill ok={rec.residual === 0}
                          okText="Headcount reconciles"
                          badText={`Residual ${rec.residual > 0 ? '+' : ''}${rec.residual} head`} />}
    >
      {rec && (
        <p className="text-sm text-gray-700 mb-4">
          {fmt(rec.opening)} opening + {rec.births} born + {rec.purchases} purchased
          − {rec.deaths} died − {rec.lost} lost − {rec.sales} sold
          = <span className="font-medium">{fmt(rec.computed_closing)}</span>
          {' '}(register shows {fmt(rec.actual_closing)}). <span className="text-gray-500">{rec.note}</span>
        </p>
      )}
      {unseen && unseen.measured && unseen.unseen_count > 0 && (
        <div className={`mb-4 p-3 rounded-lg border text-sm ${
          unseen.excess_unseen > 0
            ? 'bg-amber-50 border-amber-200 text-amber-900'
            : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          <p className="font-medium mb-1">
            {unseen.excess_unseen > 0
              ? `${unseen.unseen_count} animal${unseen.unseen_count === 1 ? '' : 's'} with no
                 recorded sighting in over ${unseen.threshold_days} days —
                 ${unseen.excess_unseen} more than the ~${unseen.expected_unseen_by_chance}
                 the random weekly sample explains. Possible unrecorded losses; worth a
                 physical check.`
              : `${unseen.unseen_count} animal${unseen.unseen_count === 1 ? '' : 's'} not seen
                 in over ${unseen.threshold_days} days — within the
                 ~${unseen.expected_unseen_by_chance} the random weekly sample is expected
                 to miss. Stalest listed for targeting.`}
          </p>
          <ul className="space-y-0.5">
            {(unseen.unseen || []).slice(0, 8).map((a, i) => (
              <li key={i}>
                {a.name} ({a.tag || 'no tag'}) — last seen {a.last_seen}, {a.days_unseen} days ago
              </li>
            ))}
          </ul>
          {unseen.unseen_count > 8 && (
            <p className="text-xs mt-1">…and {unseen.unseen_count - 8} more (stalest first).</p>
          )}
        </div>
      )}
      {unseen && unseen.measured && unseen.unseen_count === 0 && (
        <p className="text-sm text-green-800 mb-4">
          <CheckCircle2 className="inline h-4 w-4 mr-1" />
          Every active animal seen within {unseen.threshold_days} days (2 weighing cycles).
        </p>
      )}
      {weights && (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="px-3 py-2 rounded-lg bg-gray-50 border">
              Herd biomass ≈ <span className="font-medium">{fmt(weights.biomass_kg)} kg</span>
              {weights.unweighed_count > 0 && (
                <span className="text-gray-500"> ({weights.unweighed_count} never weighed)</span>
              )}
            </div>
            {weights.rolling_coverage_pct !== null && weights.rolling_coverage_pct !== undefined && (
              <div className="px-3 py-2 rounded-lg bg-gray-50 border">
                Weighed within the {Math.round((weights.cycle_days || 0) / 7)}-week cycle:{' '}
                <span className="font-medium">{weights.rolling_coverage_pct}%</span>
                {weights.overdue_for_weighing > 0 && (
                  <span className="text-amber-700"> · {weights.overdue_for_weighing} overdue — target these next</span>
                )}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="text-left text-gray-500 border-b">
                <th className="py-1 pr-4">Class</th><th className="py-1 pr-4 text-right">Head</th>
                <th className="py-1 pr-4 text-right">Avg weight (kg)</th>
                <th className="py-1 text-right">Overdue for weighing</th>
              </tr></thead>
              <tbody>
                {(weights.classes || []).map((c, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-1 pr-4 capitalize">{c.category}</td>
                    <td className="py-1 pr-4 text-right">{c.n_active}</td>
                    <td className="py-1 pr-4 text-right">{c.avg_kg ?? '—'}</td>
                    <td className="py-1 text-right">{c.overdue_for_weighing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">{weights.note}</p>
        </>
      )}
    </SectionCard>
  )
}

// Phase 4a: registration at the crush. Renders only when the ranch has a
// SmartKraal farm row (facts.registration.available); older weeks omit the key.
function RegistrationSection({ reg }) {
  if (!reg || !reg.available) return null
  const cov = reg.coverage || {}
  const methods = Object.entries(reg.bound_by_method || {})
  const METHOD = { crush_entry: 'tagging day', photo_match: 'photo match', proposal_accepted: 'proposal',
    new_animal: 'new animal', import: 'import' }
  const quiet = (reg.sent_back_open || 0) === 0 && (reg.flagged || 0) === 0
  return (
    <SectionCard
      icon={Tag} title="Registration at the crush"
      badge={<Pill ok={quiet} okText="Nothing sent back or flagged"
                   badText={`${reg.sent_back_open || 0} sent back · ${reg.flagged || 0} flagged`} />}
    >
      <p className="text-sm text-gray-700 mb-3" data-testid="reg-week-line">
        This week: <span className="font-medium">{reg.bound}</span> tag{reg.bound === 1 ? '' : 's'} bound
        {methods.length > 0 && (
          <span className="text-gray-500"> ({methods.map(([m, n]) => `${n} ${METHOD[m] || m}`).join(', ')})</span>
        )}
        {reg.resubmitted > 0 && <span> incl. {reg.resubmitted} resubmitted</span>},{' '}
        {reg.confirmed} confirmed, {reg.sent_back} sent back, {reg.retired} retired
        {reg.retired_wrong_animal > 0 && <span> ({reg.retired_wrong_animal} as the wrong animal)</span>};{' '}
        {reg.new_animals} new animal{reg.new_animals === 1 ? '' : 's'} registered from the crush record;{' '}
        {reg.weights_linked} SmartKraal weight{reg.weights_linked === 1 ? '' : 's'} linked
        {reg.animals_weighed > 0 && <span> on {reg.animals_weighed} animal{reg.animals_weighed === 1 ? '' : 's'}</span>}
        {reg.late_arrivals > 0 && (
          <span className="text-amber-700"> (+{reg.late_arrivals} from the week before that arrived after its freeze)</span>
        )}.
      </p>
      <div className="flex flex-wrap gap-4 mb-4 text-sm">
        <div className="px-3 py-2 rounded-lg bg-gray-50 border" data-testid="reg-coverage">
          <span className="font-medium">{fmt(cov.covered)} of {fmt(cov.active)}</span> active animals wear a bound or
          confirmed tag{cov.pct != null && <span> ({cov.pct}%)</span>}
        </div>
        <div className="px-3 py-2 rounded-lg bg-gray-50 border">
          {reg.awaiting_review} awaiting review · {reg.sent_back_open} sent back open
        </div>
      </div>
      {(reg.by_owner_segment || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-4">Owner</th><th className="py-1 pr-4 text-right">Animals</th>
              <th className="py-1 pr-4 text-right">With a tag</th><th className="py-1 text-right">Coverage</th>
            </tr></thead>
            <tbody>
              {reg.by_owner_segment.map((s, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1 pr-4">{s.segment}</td>
                  <td className="py-1 pr-4 text-right">{fmt(s.animals)}</td>
                  <td className="py-1 pr-4 text-right">{fmt(s.bound)}</td>
                  <td className="py-1 text-right">{s.pct == null ? '—' : `${s.pct}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">
        As of the report run: {reg.flagged} binding{reg.flagged === 1 ? '' : 's'} flagged by the checks,{' '}
        {reg.unbound_tags_with_weights} weighed tag{reg.unbound_tags_with_weights === 1 ? '' : 's'} no animal wears yet
        {reg.data_as_of_utc && <span> (kraal data as of {String(reg.data_as_of_utc).slice(0, 10)})</span>}.
        {' '}{reg.note}
      </p>
    </SectionCard>
  )
}

function ReproSection({ repro }) {
  if (!repro) return null
  return (
    <SectionCard
      icon={Baby} title="Pregnancies & births"
      badge={<Pill ok={(repro.overdue_dams || []).length === 0}
                   okText="No overdue calvings"
                   badText={`${repro.overdue_dams.length} overdue calving(s)`} />}
    >
      <div className="flex flex-wrap gap-4 text-sm mb-3">
        <span>Pregnant now: <b>{repro.pregnant_now}</b></span>
        <span>Outcome unknown: <b>{repro.pregnancy_unknown}</b></span>
        <span>Due in 30 days: <b>{repro.expected_next_30d}</b></span>
        <span>Births this week: <b>{repro.births_this_week}</b></span>
      </div>
      {(repro.overdue_dams || []).length > 0 && (
        <ul className="text-sm text-amber-800 space-y-1">
          {repro.overdue_dams.map((d, i) => (
            <li key={i}>{d.animal} ({d.rfid}) — expected {d.expected}, {d.days_overdue} days overdue</li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

function HealthSection({ health }) {
  if (!health) return null
  return (
    <SectionCard
      icon={HeartPulse} title="Health, deaths & drugs"
      badge={<Pill ok={(health.deaths_detail || []).length === 0}
                   okText="No deaths this week"
                   badText={`${health.deaths_detail.length} death(s)/lost`} />}
    >
      {(health.deaths_detail || []).length > 0 && (
        <ul className="text-sm space-y-1 mb-3">
          {health.deaths_detail.map((d, i) => (
            <li key={i}>
              {d.date} — {d.animal} ({d.rfid}), {d.cause}{' '}
              {d.evidence ? <span className="text-green-700">photo ✓</span>
                : <span className="text-amber-700">no photo evidence</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm text-gray-700 mb-2">
        Treatments: <b>{health.treatments_curative}</b> curative on {health.curative_animals} animal(s)
        {' '}· {health.treatments_routine} routine (dips/deworming/vaccination campaigns).
      </p>
      {(health.drug_alerts || []).length > 0 ? (
        <ul className="text-sm text-amber-800 space-y-1">
          {health.drug_alerts.map((a, i) => (
            <li key={i}>[{a.severity}] {a.title}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">Drug inventory balances with recorded treatments.</p>
      )}
    </SectionCard>
  )
}

function LandSection({ land }) {
  if (!land) return null
  const locked = land.week_lock_status === 'locked'
  return (
    <SectionCard
      icon={Trees} title="Grazing & paddocks"
      badge={<Pill ok={locked} okText="Week locked"
                   badText={`Calendar ${land.week_lock_status}`} />}
    >
      <div className="flex flex-wrap gap-2 mb-3 text-sm">
        {Object.entries(land.paddocks_by_status || {}).map(([s, n]) => (
          <span key={s} className="px-3 py-1 rounded-full bg-gray-100 capitalize">{s}: {n}</span>
        ))}
      </div>
      {(land.vegetation_flags || []).length > 0 ? (
        <ul className="text-sm text-amber-800 space-y-1">
          {land.vegetation_flags.map((f, i) => <li key={i}>[{f.severity}] {f.title}</li>)}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">No open vegetation or rotation flags.</p>
      )}
    </SectionCard>
  )
}

const STATE_CHIP = {
  grazed_recently: 'bg-amber-100 text-amber-900',
  recovering: 'bg-gray-100 text-gray-700',
  ready: 'bg-green-100 text-green-800',
  attention: 'bg-red-100 text-red-800',
}

function GrazingTrackerSection({ gt }) {
  if (!gt || gt.available === false) return null
  const attention = (gt.paddocks || []).filter(p => p.state === 'attention')
  const flagged = attention.length + (gt.unplanned_grazing || []).length
  const stateLabel = (s) =>
    (gt.paddocks || []).find(p => p.state === s)?.state_label || s
  return (
    <SectionCard
      icon={Footprints} title="Grazing tracker"
      badge={<Pill ok={flagged === 0} okText="Nothing needs a look"
                   badText={`${flagged} satellite flag(s)`} />}
    >
      {gt.rotation_line && <p className="text-sm text-gray-700 mb-3">{gt.rotation_line}</p>}
      <div className="flex flex-wrap gap-2 mb-3 text-sm">
        {Object.entries(gt.states || {}).map(([s, n]) => (
          <span key={s} className={`px-3 py-1 rounded-full ${STATE_CHIP[s] || 'bg-gray-100'}`}>
            {stateLabel(s)}: {n}
          </span>
        ))}
      </div>
      {(gt.unplanned_grazing || []).length > 0 && (
        <ul className="text-sm text-red-800 space-y-1 mb-3">
          {gt.unplanned_grazing.map((u, i) => (
            <li key={i}>[{u.severity}] {u.paddock ? `${u.paddock} — ` : ''}{u.title}</li>
          ))}
        </ul>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-1 pr-4">Paddock</th><th className="py-1 pr-4">State</th>
            <th className="py-1 pr-4 text-right">Last grazed</th>
            <th className="py-1 pr-4 text-right">Rest days</th>
            <th className="py-1 pr-4 text-right">Recovery</th>
            <th className="py-1 text-center">Satellite</th>
          </tr></thead>
          <tbody>
            {(gt.paddocks || []).map((p, i) => (
              <tr key={i} className={`border-b border-gray-100 ${p.state === 'attention' ? 'bg-red-50' : ''}`}>
                <td className="py-1 pr-4 whitespace-nowrap">{p.name || p.paddock}</td>
                <td className="py-1 pr-4 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${STATE_CHIP[p.state] || 'bg-gray-100'}`}>
                    {p.state_label}
                  </span>
                </td>
                <td className="py-1 pr-4 text-right whitespace-nowrap">
                  {p.days_since_grazed != null ? `${p.days_since_grazed}d ago` : '—'}
                </td>
                <td className="py-1 pr-4 text-right">{p.rest_days ?? '—'}</td>
                <td className="py-1 pr-4 text-right">{p.recovery_pct != null ? `${p.recovery_pct}%` : '—'}</td>
                <td className="py-1 text-center">{p.satellite_verified
                  ? <CheckCircle2 className="h-4 w-4 text-green-700 inline" /> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}

function RollupView({ ranchId, granularity }) {
  const { data, isLoading } = useQuery({
    queryKey: ['weeklyRollup', ranchId, granularity],
    queryFn: () => getWeeklyRollup(ranchId, granularity),
    enabled: !!ranchId,
  })
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>
  const buckets = data?.buckets || []
  if (!buckets.length) return <p className="text-sm text-gray-500">No weekly reports stored yet for this view.</p>
  return (
    <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead><tr className="text-left text-gray-500 border-b">
          <th className="py-1 pr-4">Period</th><th className="py-1 pr-4 text-right">Weeks</th>
          <th className="py-1 pr-4 text-right">Income</th><th className="py-1 pr-4 text-right">Expenses</th>
          <th className="py-1 pr-4 text-right">Net</th><th className="py-1 pr-4 text-right">Births</th>
          <th className="py-1 pr-4 text-right">Deaths</th><th className="py-1 pr-4 text-right">Sales</th>
          <th className="py-1 pr-4 text-right">Avg ADG</th><th className="py-1 pr-4 text-right">Herd end</th>
          <th className="py-1 text-right">Trust</th>
        </tr></thead>
        <tbody>
          {[...buckets].reverse().map((b, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="py-1 pr-4 whitespace-nowrap">{b.bucket}</td>
              <td className="py-1 pr-4 text-right">{b.weeks}</td>
              <td className="py-1 pr-4 text-right">{fmt(b.income)}</td>
              <td className="py-1 pr-4 text-right">{fmt(b.expenses)}</td>
              <td className={`py-1 pr-4 text-right ${b.net < 0 ? 'text-amber-700' : ''}`}>{fmt(b.net)}</td>
              <td className="py-1 pr-4 text-right">{b.births}</td>
              <td className="py-1 pr-4 text-right">{b.deaths}</td>
              <td className="py-1 pr-4 text-right">{b.sales}</td>
              <td className="py-1 pr-4 text-right">{b.avg_daily_gain_kg ?? '—'}</td>
              <td className="py-1 pr-4 text-right">{b.herd_end ?? '—'}</td>
              <td className="py-1 text-right">{b.trust_score ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function WeeklyReport({ ranchId }) {
  const [granularity, setGranularity] = useState('weekly')
  const [selectedId, setSelectedId] = useState(null)

  const { data: list } = useQuery({
    queryKey: ['weeklyReports', ranchId],
    queryFn: () => getWeeklyReports(ranchId),
    enabled: !!ranchId,
  })
  // Ranch-wide weeks only would need owner_segment filtering server-side; the
  // list is already newest-first and the detail carries the full facts.
  const items = list?.items || []
  const activeId = selectedId || items[0]?.id

  const { data: report, isLoading } = useQuery({
    queryKey: ['weeklyReport', activeId],
    queryFn: () => getWeeklyReport(activeId),
    enabled: !!activeId && granularity === 'weekly',
  })

  const facts = report?.facts || {}

  // Live streak strip (frozen per-report recent_weeks is the fallback).
  const { data: strip } = useQuery({
    queryKey: ['scorecards', ranchId],
    queryFn: () => getScorecards(ranchId, 12),
    enabled: !!ranchId && granularity === 'weekly',
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weekly Report</h1>
          <p className="text-sm text-gray-500">
            The Tuesday Report — last week on your ranch, every week.{' '}
            <Link to="/reports" className="text-green-700 underline">Detailed monthly reports →</Link>
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {GRANULARITIES.map(g => (
            <button key={g.key} onClick={() => setGranularity(g.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                granularity === g.key ? 'bg-white shadow text-gray-900' : 'text-gray-600'}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {granularity === 'incentive' ? (
        <IncentiveView ranchId={ranchId} />
      ) : granularity !== 'weekly' ? (
        <RollupView ranchId={ranchId} granularity={granularity} />
      ) : (
        <>
          {items.length > 1 && (
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="h-4 w-4 text-gray-500" />
              <select value={activeId || ''} onChange={e => setSelectedId(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1">
                {items.map(it => (
                  <option key={it.id} value={it.id}>{it.period_label}</option>
                ))}
              </select>
            </div>
          )}

          {isLoading && <p className="text-sm text-gray-500">Loading report…</p>}
          {!isLoading && !report && (
            <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">
              No weekly report yet — the first one generates automatically on Tuesday morning.
            </div>
          )}

          {report && (
            <>
              <ScorecardSection scorecard={facts.scorecard} strip={strip} />
              {report.narrative && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm whitespace-pre-line">
                  {report.narrative}
                </div>
              )}
              <MoneySection money={facts.money} />
              <HerdSection rec={facts.herd_reconciliation} weights={facts.weights_by_class}
                           unseen={facts.unseen_animals} />
              <RegistrationSection reg={facts.registration} />
              <ReproSection repro={facts.reproduction} />
              <HealthSection health={facts.health} />
              <GrazingTrackerSection gt={facts.grazing_tracker} />
              <LandSection land={facts.land} />
              <TrajectorySection twin={facts.trajectories} />
            </>
          )}
        </>
      )}
    </div>
  )
}
